import { ConflictType } from '@consigli/types';
import { uniqueBy } from '@consigli/utils';

import { type MessageSource } from '@/util/interfaces';

const CONFLICT_TYPES = new Set<unknown>(Object.values(ConflictType));

const isConflictType = (value: unknown): value is ConflictType => CONFLICT_TYPES.has(value);

/** A v2 entry is the id itself, a v3 entry an object carrying it. */
const entryId = (entry: unknown): unknown =>
  typeof entry === 'object' && entry !== null ? (entry as { id?: unknown }).id : entry;

const SOURCE_NAME_PREFIX_LENGTH = 14;
const SOURCE_NAME_SUFFIX_LENGTH = 4;
const SOURCE_NAME_ELLIPSIS = '...';

export const truncateSourceName = (name: unknown): string => {
  if (typeof name !== 'string' || name.length === 0) return '';

  const extensionStart = name.lastIndexOf('.');
  const hasExtension = extensionStart > 0 && extensionStart < name.length - 1;
  const basename = hasExtension ? name.slice(0, extensionStart) : name;
  const extension = hasExtension ? name.slice(extensionStart) : '';
  const basenameCharacters = Array.from(basename);

  return basenameCharacters.length >
    SOURCE_NAME_PREFIX_LENGTH + SOURCE_NAME_SUFFIX_LENGTH + SOURCE_NAME_ELLIPSIS.length
    ? `${basenameCharacters.slice(0, SOURCE_NAME_PREFIX_LENGTH).join('')}${SOURCE_NAME_ELLIPSIS}${basenameCharacters.slice(-SOURCE_NAME_SUFFIX_LENGTH).join('')}${extension}`
    : name;
};

/**
 * The chat stream sends an answer's sources in one of three shapes.
 *
 * v1 sends a citation per matched passage, so one document arrives many times
 * over - a recent answer carried 25 entries for 5 documents - each with the
 * passage text, and marks a finding with a findingId and no blobId.
 *
 * v2 sends ids alone, already deduplicated, with documents and findings split
 * into their own lists. v3 sends those lists as objects under new names.
 *
 * Both kinds are returned, deduplicated within their kind, in documents-then-
 * findings order. conversation_id is read by nothing yet.
 */
export const parseStreamSources = (sources: unknown): MessageSource[] => {
  if (Array.isArray(sources)) {
    const documents = uniqueBy(
      sources
        .filter(
          (source: { blobId?: unknown }): source is { blobId: string; content?: string } =>
            typeof source?.blobId === 'string' && source.blobId !== '',
        )
        .map(({ blobId, content }) => ({ blobId, content })),
      (source) => source.blobId,
    );

    /*
     * A finding citation is the one with no blobId - an entry carrying both
     * belongs to the document it cites, and counting it twice would show the
     * same source under either heading.
     */
    const findings = uniqueBy(
      sources
        .filter(
          (source: { blobId?: unknown; findingId?: unknown }): source is { findingId: number } =>
            typeof source?.findingId === 'number' && typeof source?.blobId !== 'string',
        )
        .map(({ findingId }) => ({ findingId })),
      (source) => source.findingId,
    );

    return [...documents, ...findings];
  }

  /*
   * v3 (INS-800) renames both lists and sends each entry as an object, a
   * finding's with its conflict type. Every spelling stays readable so the
   * sources still show on a backend that has not shipped it.
   */
  const payload = sources as {
    documents?: unknown;
    document_ids?: unknown;
    documentIds?: unknown;
    findings?: unknown;
    finding_ids?: unknown;
    findingIds?: unknown;
  } | null;
  const documentEntries = payload?.documents ?? payload?.document_ids ?? payload?.documentIds;
  const findingEntries = payload?.findings ?? payload?.finding_ids ?? payload?.findingIds;

  const documents = Array.isArray(documentEntries)
    ? uniqueBy(
        documentEntries.flatMap((entry: unknown) => {
          const blobId = entryId(entry);
          return typeof blobId === 'string' && blobId !== '' ? [{ blobId }] : [];
        }),
        (source) => source.blobId,
      )
    : [];

  const findings = Array.isArray(findingEntries)
    ? uniqueBy(
        findingEntries.flatMap((entry: unknown): MessageSource[] => {
          const findingId = entryId(entry);
          if (typeof findingId !== 'number') return [];
          const conflictType = (entry as { conflict_type?: unknown } | null)?.conflict_type;
          return [isConflictType(conflictType) ? { findingId, conflictType } : { findingId }];
        }),
        (source) => source.findingId,
      )
    : [];

  return [...documents, ...findings];
};
